import type { BehaviorSignal } from '../../contracts/signals';
import type { SessionSummaryAccumulatorState } from '../../contracts/sessionSummary';

/**
 * Acumula sinais internos da sessão em estruturas limitadas (Etapa 20).
 * Não guarda a lista de sinais; apenas contadores e durações derivadas de
 * transições de estado. Durações são computadas por "flush": o tempo decorrido
 * desde o último flush é atribuído ao bucket do estado atual.
 */
export class SessionSummaryAccumulator {
  private startedAt = 0;

  private lastFlushAt = 0;

  private lastSignalAt: number | undefined;

  private activeDurationMs = 0;

  private idleDurationMs = 0;

  private visibleDurationMs = 0;

  private hiddenDurationMs = 0;

  private viewportVisibleDurationMs = 0;

  private maxScrollPercent = 0;

  private clickCount = 0;

  private rageClickCount = 0;

  private viewportEntryCount = 0;

  private heartbeatCount = 0;

  private isIdle = false;

  private isVisible = true;

  private activeViewportCount = 0;

  reset(now: number): void {
    this.startedAt = now;
    this.lastFlushAt = now;
    this.lastSignalAt = undefined;
    this.activeDurationMs = 0;
    this.idleDurationMs = 0;
    this.visibleDurationMs = 0;
    this.hiddenDurationMs = 0;
    this.viewportVisibleDurationMs = 0;
    this.maxScrollPercent = 0;
    this.clickCount = 0;
    this.rageClickCount = 0;
    this.viewportEntryCount = 0;
    this.heartbeatCount = 0;
    this.isIdle = false;
    this.isVisible = true;
    this.activeViewportCount = 0;
  }

  /** Consolida o tempo decorrido desde o último flush nos buckets atuais. */
  flush(now: number): void {
    const elapsed = Math.max(0, now - this.lastFlushAt);
    if (elapsed > 0) {
      if (this.isIdle) {
        this.idleDurationMs += elapsed;
      } else {
        this.activeDurationMs += elapsed;
      }

      if (this.isVisible) {
        this.visibleDurationMs += elapsed;
      } else {
        this.hiddenDurationMs += elapsed;
      }

      if (this.activeViewportCount > 0) {
        this.viewportVisibleDurationMs += elapsed;
      }
    }
    this.lastFlushAt = now;
  }

  ingest(signal: BehaviorSignal): void {
    const now = signal.timestamp;
    this.flush(now);
    this.lastSignalAt = now;

    switch (signal.type) {
      case 'HEARTBEAT':
        this.heartbeatCount += 1;
        break;
      case 'CLICK':
        this.clickCount += 1;
        break;
      case 'RAGE_CLICK':
        this.rageClickCount += 1;
        break;
      case 'SCROLL': {
        const percent = signal.data?.maxScrollPercent;
        if (typeof percent === 'number' && Number.isFinite(percent)) {
          this.maxScrollPercent = Math.min(
            100,
            Math.max(this.maxScrollPercent, percent)
          );
        }
        break;
      }
      case 'IDLE':
        this.isIdle = true;
        break;
      case 'ACTIVITY':
        this.isIdle = false;
        break;
      case 'VISIBLE':
        this.isVisible = true;
        break;
      case 'HIDDEN':
        this.isVisible = false;
        break;
      case 'VIEWPORT_ENTER':
        this.viewportEntryCount += 1;
        this.activeViewportCount += 1;
        break;
      case 'VIEWPORT_EXIT':
        if (this.activeViewportCount > 0) {
          this.activeViewportCount -= 1;
        }
        break;
      default:
        break;
    }
  }

  getState(): SessionSummaryAccumulatorState {
    return {
      activeDurationMs: this.activeDurationMs,
      idleDurationMs: this.idleDurationMs,
      visibleDurationMs: this.visibleDurationMs,
      hiddenDurationMs: this.hiddenDurationMs,
      maxScrollPercent: this.maxScrollPercent,
      clickCount: this.clickCount,
      rageClickCount: this.rageClickCount,
      viewportEntryCount: this.viewportEntryCount,
      viewportVisibleDurationMs: this.viewportVisibleDurationMs,
      heartbeatCount: this.heartbeatCount,
      startedAt: this.startedAt,
      lastSignalAt: this.lastSignalAt,
    };
  }
}
